import { cn } from '@/shared/lib/utils';
import { Checkbox } from '@/shared/ui/checkbox/Checkbox';
import type { SelectOption } from '../helpers/types';
import { normalizeOptionValue } from '../helpers/utils';

type MultipleSelectPopoverContentProps = {
  filteredOptions: SelectOption[];
  selectedValues: string[];
  toggleValue: (optionValue: string | number) => void;
  activeIndex: number;
  id?: string;
  listboxId?: string;
};

export const MultipleSelectPopoverContent = ({
  filteredOptions,
  selectedValues,
  toggleValue,
  activeIndex,
  id,
  listboxId,
}: MultipleSelectPopoverContentProps) => {
  return (
    <div
      role="listbox"
      id={listboxId}
      aria-multiselectable="true"
      // listbox 自身は max-h-64 で上限 16rem。
      // 親 PopoverContent が flex container かつ boundary 配下で max-height 制限されている場合、
      // flex-1 + min-h-0 によって listbox が外側 PopoverContent 内に自動収縮する。
      // この仕組みにより、外側 padding を考慮した上で内側 scroll に切り替わる（外側 padding 込みで親枠を超えない）。
      className="max-h-64 min-h-0 flex-1 overflow-y-auto"
    >
      {filteredOptions.length > 0 ? (
        filteredOptions.map((option, index) => {
          const normalizedValue = normalizeOptionValue(option.value);
          const isSelected = selectedValues.includes(normalizedValue);
          return (
            <div
              key={normalizedValue}
              role="option"
              id={id ? `${id}-option-${index}` : undefined}
              aria-selected={isSelected}
              tabIndex={-1}
              onClick={() => toggleValue(option.value)}
              onMouseDown={(e) => e.preventDefault()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleValue(option.value);
                }
              }}
              className={cn(
                'flex w-full cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-left text-sm',
                'hover:bg-muted',
                index === activeIndex && 'bg-surface-accent',
              )}
            >
              <Checkbox checked={isSelected} aria-hidden />
              <span className="flex-1">{option.label}</span>
            </div>
          );
        })
      ) : (
        <div className="py-6 text-center text-sm text-text-muted">検索結果がありません</div>
      )}
    </div>
  );
};
